import { useMemo } from "react";
import { Folder, Check } from "lucide-react";
import { useEditorStore } from "../../../../store/editorStore";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  layerIcon,
  spineStaticOriginalUrl,
  spineStaticThumbUrl,
} from "commons/cdn";
import { SkinThumbnail } from "../../../../components/SkinThumbnail";
import { SelectionGroupLabel } from "./SelectionGroupLabel";

import { SelectionData } from "../../../../types/selection";

function thumbSrcForStatic(sku: any): string {
  if (sku.thumbAssetId) return spineStaticThumbUrl(sku.thumbAssetId);
  if (sku.originalAssetId) return spineStaticOriginalUrl(sku.originalAssetId);
  return "";
}

// 关联模版使用的只读 Selection Item 组件
export function RelatedSelectionItem({
  selection,
  spineData,
}: {
  selection: SelectionData & {
    skusMap?: Record<string, any>;
    relatedTemplateId?: string;
  };
  spineData: any;
}) {
  const { selectedSkins, toggleSkin } = useEditorStore();

  // 从 selection.skuIds 获取 SKU 列表
  const skuIds = selection.skuIds || [];
  // 关联模版使用传入的 skusMap
  const skus = selection.skusMap || {};
  const skuList = useMemo(() => {
    return skuIds.map((skuId: string) => skus[skuId]).filter(Boolean);
  }, [skuIds, skus]);

  // 计算当前 selection 中已选中的数量
  const selectedCount = skuList.filter((sku: any) =>
    selectedSkins.includes(sku.skinName),
  ).length;

  return (
    <AccordionItem
      value={selection.id}
      className="group border border-dashed border-muted-foreground/30 rounded-lg mb-2 bg-muted/10"
    >
      <AccordionTrigger className="px-3 py-2 hover:no-underline">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* 只读标记 */}
          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-mono text-muted-foreground shrink-0">
            R
          </div>

          {/* LayerIcon (只读) */}
          <div className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md">
            {selection.layerIconId ? (
              <img
                src={layerIcon(selection.layerIconId)}
                alt="LayerIcon"
                className="h-6 w-6 object-contain"
              />
            ) : (
              <Folder className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {/* Selection 名称 */}
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <span className="font-medium text-sm truncate">
              {selection.name || selection.id}
            </span>
            <span className="text-[10px] bg-muted px-1 rounded text-muted-foreground shrink-0">
              {selection.relatedTemplateId}
            </span>
          </div>

          <SelectionGroupLabel gid={selection.gid} />

          {/* 数量徽章 */}
          <div className="flex items-center gap-1 ml-auto mr-2 shrink-0">
            <Badge
              variant={selectedCount > 0 ? "default" : "secondary"}
              className="h-5 px-1.5 text-[10px]"
            >
              {selectedCount}/{skuIds.length}
            </Badge>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        <div className="grid grid-cols-3 gap-2">
          {skuList.map((sku: any) => {
            const category = sku.category || "";
            const isSelected = selectedSkins.includes(sku.skinName);
            const staticThumb =
              sku.category === "static" ? thumbSrcForStatic(sku) : "";
            const dynamicCustomThumb =
              sku.category !== "static" && sku.thumbAssetId
                ? spineStaticThumbUrl(sku.thumbAssetId)
                : "";
            return (
              <div
                key={sku.id}
                className={cn(
                  "relative rounded-lg border p-1.5 transition-colors cursor-pointer",
                  "bg-muted/30 hover:bg-muted/50",
                )}
                onClick={() => toggleSkin(sku.skinName, selection.id)}
              >
                {/* 缩略图：自定义封面 / 静态 CDN / ZIP 渲染 */}
                <div className="flex justify-center mb-1">
                  {sku.category === "static" ? (
                    staticThumb ? (
                      <img
                        src={staticThumb}
                        alt=""
                        className="w-14 h-14 object-contain rounded border bg-muted/30"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded border bg-muted/40 flex items-center justify-center text-[10px] text-muted-foreground">
                        无图
                      </div>
                    )
                  ) : dynamicCustomThumb ? (
                    <img
                      src={dynamicCustomThumb}
                      alt=""
                      className="w-14 h-14 object-contain rounded border bg-muted/30"
                    />
                  ) : (
                    <SkinThumbnail
                      skinName={sku.skinName}
                      size={56}
                      spineData={spineData}
                    />
                  )}
                </div>
                {/* Category + SKU ID */}
                <div className="text-center space-y-0.5">
                  <span className="truncate block text-[10px] text-muted-foreground">
                    {category}
                  </span>
                  <span className="truncate block text-[9px] font-mono text-muted-foreground/60">
                    {sku.id}
                  </span>
                </div>
                {/* 价格 */}
                <div className="flex items-center justify-center gap-1 mt-0.5">
                  <Badge
                    variant={(sku.pro || 0) > 0 ? "default" : "secondary"}
                    className={cn(
                      "text-[10px] px-1.5 h-4",
                      (sku.pro || 0) > 0 ? "bg-amber-500" : "",
                    )}
                  >
                    {(sku.pro || 0) > 0 ? `¥${sku.pro}` : "免费"}
                  </Badge>
                </div>
                {/* 选中标记 */}
                {isSelected && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
